import { useEffect, useMemo, useRef, useState } from 'react';
import MiniSearch from 'minisearch';
import { useNotesStore, type Note, type NoteContentType } from '@/store/notesStore';
import { getCachedNoteBodies } from '@/services/NoteBodyCache';

type SearchDoc = {
  id: string;
  title: string;
  tags: string[];
  content: string;
  pinned?: boolean;
  updated_at?: string;
  content_type?: NoteContentType;
  content_status?: 'draft' | 'final';
};

const SEARCH_FIELDS: Array<keyof Pick<SearchDoc, 'title' | 'tags' | 'content'>> = [
  'title',
  'tags',
  'content',
];

const STORE_FIELDS: Array<keyof SearchDoc> = [
  'id',
  'title',
  'pinned',
  'updated_at',
  'content_type',
  'content_status',
  'tags',
];

function getSearchDocId(note: Note, index: number): string {
  return note.id ?? `local-note-${index}`;
}

function createSearchDoc(note: Note, index: number): SearchDoc {
  const tags = note.tags ?? [];

  return {
    id: getSearchDocId(note, index),
    title: note.title ?? '',
    tags,
    content: note.encrypted
      ? (note.body_locked ? '' : [...new Set([note.content, ...(note.id ? getCachedNoteBodies(note.id) : [])])].join('\n'))
      : note.content,
    pinned: note.pinned,
    updated_at: note.content_updated_at ?? note.date,
    content_type: note.content_type,
    content_status: note.content_status,
  };
}

function getDocSignature(doc: SearchDoc): string {
  return JSON.stringify(doc);
}

export function useNotesSearch() {
  const notes = useNotesStore((state) => state.notes);
  const [query, setQuery] = useState('');
  const prevIdsRef = useRef<Set<string>>(new Set());
  const prevDocsRef = useRef<Map<string, SearchDoc>>(new Map());

  const miniSearch = useMemo(
    () =>
      new MiniSearch<SearchDoc>({
        fields: SEARCH_FIELDS,
        storeFields: STORE_FIELDS,
        extractField: (document, fieldName) => {
          if (fieldName === 'tags') {
            return document.tags.join(' ');
          }

          return document[fieldName as keyof SearchDoc];
        },
        searchOptions: {
          boost: {
            title: 5,
            tags: 3,
            content: 1,
          },
          prefix: true,
        },
      }),
    [],
  );

  useEffect(() => {
    const nextDocs = new Map<string, SearchDoc>();

    notes.forEach((note, index) => {
      const doc = createSearchDoc(note, index);
      nextDocs.set(doc.id, doc);
    });

    const prevIds = prevIdsRef.current;
    const prevDocs = prevDocsRef.current;

    prevIds.forEach((id) => {
      if (!nextDocs.has(id)) {
        miniSearch.discard(id);
      }
    });

    nextDocs.forEach((doc, id) => {
      const prevDoc = prevDocs.get(id);

      if (!prevIds.has(id)) {
        miniSearch.add(doc);
        return;
      }

      if (prevDoc && getDocSignature(prevDoc) !== getDocSignature(doc)) {
        miniSearch.replace(doc);
      }
    });

    prevIdsRef.current = new Set(nextDocs.keys());
    prevDocsRef.current = nextDocs;
  }, [miniSearch, notes]);

  const results = useMemo<Note[] | null>(() => {
    const trimmedQuery = query.trim();

    if (!trimmedQuery) {
      return null;
    }

    const notesBySearchId = new Map<string, Note>();
    notes.forEach((note, index) => {
      notesBySearchId.set(getSearchDocId(note, index), note);
    });

    return miniSearch
      .search(trimmedQuery)
      .map((hit) => notesBySearchId.get(String(hit.id)))
      .filter((note): note is Note => Boolean(note));
  }, [miniSearch, notes, query]);

  return {
    query,
    setQuery,
    results,
  };
}
